import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LocateFixed } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, BigButton } from "@/components/Field";
import { SelectField, TextArea } from "@/components/Bits";
import { UFS } from "@/config/equipamentos";
import { api, errMsg } from "@/lib/api";

const EMPTY = { cliente_id: "", nome: "", municipio: "", estado: "", endereco: "", latitude: "", longitude: "", responsavel: "", telefone: "", observacoes: "" };

export const FazendaFormDialog = ({ open, onOpenChange, editing, clienteId, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [clientes, setClientes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, editing[k] ?? ""])) : { ...EMPTY, cliente_id: clienteId || "" });
    api.get("/clientes").then(({ data }) => setClientes(data)).catch(() => {});
  }, [open, editing, clienteId]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Localização não disponível neste aparelho");
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) })); toast.success("Localização capturada"); },
      () => toast.error("Não foi possível obter a localização"),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, latitude: form.latitude === "" ? null : Number(form.latitude), longitude: form.longitude === "" ? null : Number(form.longitude) };
    try {
      const { data } = editing ? await api.put(`/fazendas/${editing.fazenda_id}`, body) : await api.post("/fazendas", body);
      toast.success(editing ? "Fazenda atualizada" : "Fazenda cadastrada");
      onOpenChange(false);
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="fazenda-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar fazenda" : "Nova fazenda"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2">
            <SelectField label="Cliente *" id="f-cli" required placeholder="Selecione o cliente" value={form.cliente_id} onChange={set("cliente_id")}
              options={clientes.map((c) => ({ value: c.cliente_id, label: c.nome }))} data-testid="fazenda-form-cliente" />
          </div>
          <div className="sm:col-span-2"><Field label="Nome da fazenda *" id="f-nome" required value={form.nome} onChange={set("nome")} data-testid="fazenda-form-nome" /></div>
          <Field label="Município" id="f-mun" value={form.municipio} onChange={set("municipio")} data-testid="fazenda-form-municipio" />
          <SelectField label="Estado" id="f-uf" placeholder="UF" value={form.estado} onChange={set("estado")} options={UFS} data-testid="fazenda-form-estado" />
          <div className="sm:col-span-2"><Field label="Endereço" id="f-end" value={form.endereco} onChange={set("endereco")} data-testid="fazenda-form-endereco" /></div>
          <Field label="Latitude" id="f-lat" inputMode="decimal" value={form.latitude} onChange={set("latitude")} placeholder="-17.792300" data-testid="fazenda-form-latitude" />
          <Field label="Longitude" id="f-lng" inputMode="decimal" value={form.longitude} onChange={set("longitude")} placeholder="-50.919200" data-testid="fazenda-form-longitude" />
          <div className="sm:col-span-2">
            <BigButton type="button" variant="outline" onClick={locate} data-testid="fazenda-form-locate"><LocateFixed className="h-5 w-5" /> Usar minha localização atual</BigButton>
          </div>
          <Field label="Responsável" id="f-resp" value={form.responsavel} onChange={set("responsavel")} data-testid="fazenda-form-responsavel" />
          <Field label="Telefone" id="f-tel" type="tel" value={form.telefone} onChange={set("telefone")} data-testid="fazenda-form-telefone" />
          <div className="sm:col-span-2"><TextArea label="Observações" id="f-obs" value={form.observacoes} onChange={set("observacoes")} data-testid="fazenda-form-observacoes" /></div>
          <div className="sm:col-span-2">
            <BigButton type="submit" disabled={busy} data-testid="fazenda-form-submit">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Salvar</BigButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
